import { api } from '../config/api';

export const fleetService = {
  // Get all vehicles with filtering
  getAll: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.reason) params.append('reason', filters.reason);
    if (filters.make) params.append('make', filters.make);
    if (filters.year) params.append('year', filters.year);
    if (filters.search) params.append('search', filters.search);
    if (filters.include_retired) params.append('include_retired', 'true');
    
    const queryString = params.toString();
    return api.get(`/api/fleet${queryString ? `?${queryString}` : ''}`);
  },
  
  // Get single vehicle with full details
  getById: async (id) => {
    return api.get(`/api/fleet/${id}`);
  },
  
  // Create new vehicle
  create: async (data) => {
    return api.post('/api/fleet', data);
  },
  
  // Update vehicle
  update: async (id, data) => {
    return api.put(`/api/fleet/${id}`, data);
  },
  
  // Delete vehicle
  delete: async (id) => {
    return api.delete(`/api/fleet/${id}`);
  },
  
  // Change vehicle status
  changeStatus: async (id, data) => {
    return api.post(`/api/fleet/${id}/status`, data);
  },
  
  // Return vehicle to service
  returnToService: async (id, data) => {
    return api.post(`/api/fleet/${id}/return-to-service`, data);
  },
  
  // Get status history
  getStatusHistory: async (id) => {
    return api.get(`/api/fleet/${id}/status-history`);
  },
  
  // Add maintenance record
  addMaintenance: async (vehicleId, data) => {
    return api.post(`/api/fleet/${vehicleId}/maintenance`, data);
  },
  
  // Get fleet status summary (dashboard)
  getStatusSummary: async () => {
    return api.get('/api/fleet/status/summary');
  },
  
  // === COMPLIANCE & IMPORT ===
  
  // Import vehicles from Excel file
  importExcel: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.upload('/api/fleet/import', formData);
  },
  
  // Get compliance alerts (expiring inspections, registrations, insurance)
  getComplianceAlerts: async () => {
    return api.get('/api/fleet/compliance-alerts');
  },
  
  // Dispose/retire a vehicle
  disposeVehicle: async (id, data) => {
    return api.post(`/api/fleet/${id}/dispose`, data);
  },
  
  // === MALFUNCTION REPORTS ===
  
  // Get all malfunction reports
  getMalfunctions: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.severity) params.append('severity', filters.severity);
    if (filters.vehicle_id) params.append('vehicle_id', filters.vehicle_id);
    
    const queryString = params.toString();
    return api.get(`/api/fleet/malfunctions/all${queryString ? `?${queryString}` : ''}`);
  },
  
  // Create malfunction report
  reportMalfunction: async (data) => {
    return api.post('/api/fleet/malfunctions', data);
  },
  
  // Acknowledge malfunction
  acknowledgeMalfunction: async (id) => {
    return api.post(`/api/fleet/malfunctions/${id}/acknowledge`);
  },
  
  // Resolve malfunction
  resolveMalfunction: async (id, data) => {
    return api.post(`/api/fleet/malfunctions/${id}/resolve`, data);
  },

  // === PHASE 3: MAINTENANCE SCHEDULES ===

  // Get maintenance dashboard
  getMaintenanceDashboard: async () => {
    return api.get('/api/fleet/maintenance/dashboard');
  },

  // Get all maintenance schedules
  getMaintenanceSchedules: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.vehicle_id) params.append('vehicle_id', filters.vehicle_id);
    if (filters.active_only !== undefined) params.append('active_only', filters.active_only);
    
    const queryString = params.toString();
    return api.get(`/api/fleet/maintenance/schedules${queryString ? `?${queryString}` : ''}`);
  },

  // Create maintenance schedule
  createMaintenanceSchedule: async (data) => {
    return api.post('/api/fleet/maintenance/schedules', data);
  },

  // Update maintenance schedule
  updateMaintenanceSchedule: async (id, data) => {
    return api.put(`/api/fleet/maintenance/schedules/${id}`, data);
  },

  // Delete maintenance schedule
  deleteMaintenanceSchedule: async (id) => {
    return api.delete(`/api/fleet/maintenance/schedules/${id}`);
  },

  // Complete maintenance schedule (advance next due)
  completeMaintenanceSchedule: async (id, data) => {
    return api.post(`/api/fleet/maintenance/schedules/${id}/complete`, data);
  },

  // Get vehicles due for maintenance
  getVehiclesDueForMaintenance: async (days = 14) => {
    return api.get(`/api/fleet/maintenance/due?days=${days}`);
  },

  // === PHASE 3: WORK ORDERS ===

  // Get all work orders
  getWorkOrders: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.priority) params.append('priority', filters.priority);
    if (filters.vehicle_id) params.append('vehicle_id', filters.vehicle_id);
    if (filters.assigned_to) params.append('assigned_to', filters.assigned_to);
    
    const queryString = params.toString();
    return api.get(`/api/fleet/work-orders${queryString ? `?${queryString}` : ''}`);
  },

  // Get single work order
  getWorkOrder: async (id) => {
    return api.get(`/api/fleet/work-orders/${id}`);
  },

  // Create work order
  createWorkOrder: async (data) => {
    return api.post('/api/fleet/work-orders', data);
  },

  // Update work order (including complete)
  updateWorkOrder: async (id, data) => {
    return api.put(`/api/fleet/work-orders/${id}`, data);
  }
};
